import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Drawer from '@material-ui/core/Drawer';
import PublicRoundedIcon from '@material-ui/icons/PublicRounded';
import { confirm as confirmAction, showAlert } from 'actions/app';
import { hideReferenceReleaseComposer as hideReferenceReleaseComposerAction } from 'actions/joborder';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import {
  AIProspectingAssistantDrawer,
  AIProspectingAssistantModal
} from 'features/ai-assistant/components';
import { useProspectingAssistant } from 'features/ai-assistant/hooks';
import { generateJobOrderPresentation } from 'features/ai-generators';
import {
  CallVerbiages,
  JobOrderPresentationTypeSelector,
  JobOrderRoleplayScenarioSelector,
  PresentationMakerTemplate
} from 'features/ai-generators/components';
import { CALL_VERBIAGES_TYPES } from 'features/ai-generators/constants';
import { CreationTaskDrawer } from 'features/daily-plan';
import { TASK_TYPES } from 'features/daily-plan/constants';
import { useProfile } from 'hooks/profileActions';
import useFetchCatalogs from 'hooks/useFetchCatalogs';
import { useVerifyUrgencyDialogManager } from 'hooks/useVerifyUrgencyDialogManager';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { useUserContext } from 'providers/UserProvider';
import { EntityRoutes } from 'routes/constants';
import API from 'services/API';
import {
  canUserOrStaffEditEntity,
  Permissions,
  userHasPermission,
  userHasRoles
} from 'services/Authorization';
import { JobOrderIncludes } from 'services/includes';
import { getSendoutById } from 'services/Sendouts';
import strings from 'strings';
import FPActionButton from 'UI/components/atoms/FPActionButton';
import { When } from 'UI/components/atoms/When';
import { VerifyUrgencyDialog } from 'UI/components/molecules/VerifyUrgencyDialog';
import ReferenceRelease from 'UI/components/organisms/candidates/ReferenceRelease';
import {
  ClaimAccountabilityDrawer,
  useClaimAccountability
} from 'UI/components/organisms/inventoryProfiles/ClaimAccountability';
import { handleEntityEditionCompleted } from 'UI/components/organisms/inventoryProfiles/utils';
import JobOrderEdit from 'UI/components/organisms/JobOrderEdit';
import { PlacementCreateManager } from 'UI/components/organisms/placements/PlacementCreateManager';
import { getPlacementActions } from 'UI/components/organisms/placements/utils';
import ReassignRecruiterForm from 'UI/components/organisms/ReassignRecruiter';
import SendoutsForm from 'UI/components/organisms/sendouts/SendoutForm';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import InventoryProfileLayout from 'UI/components/templates/InventoryProfileLayout';
import { DateFormats } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { CompanyType, EntityType, JobOrderEntity, TabKeys } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { PERSISTENCE_KEYS } from 'UI/constants/persistenceKeys';
import { Roles } from 'UI/constants/roles';
import { UIStatus } from 'UI/constants/status';
import { SvgMakeAi } from 'UI/res';
import { EmptyFeeAgreement } from 'UI/res/images';
import { getErrorMessage, localTimeFormatter } from 'UI/utils';
import { completeReferenceRelease, sendoutDataToForm } from 'UI/utils/inventory';
import { updateUiState } from 'UI/utils/uiHelpers';

import { useFetchDataSheet } from '../EditDataSheet/hooks';

import { useJobOrderProfileFeatureFlags } from './JobOrderProfile.hooks';
import {
  buildJobOrderProspectingAssistantProps,
  buildPresentationMakerJobOrderParam
} from './utils';

const {
  inventoryProfiles: {
    sections: {
      jobOrders: { duplicateDialog: duplicateDialogCopies }
    }
  },
  inventory: {
    jobOrders: {
      profile: { editButton }
    }
  }
} = strings;

const createTabsProps = ({
  endpoint,
  jobOrder,
  joborderId,
  onActivityComplete,
  onNewPlacementClick,
  onSendoutClick,
  onTriggerTabRefresh,
  onWriteUpClick,
  overviewProps,
  profileModule,
  profileName,
  shouldRefresh,
  tabKeys,
  uiState
}) => [
  {
    id: TabKeys.Overview,
    tabProps: {
      profile: jobOrder,
      onWriteUpClick,
      ...overviewProps
    }
  },
  {
    id: TabKeys.Sendouts,
    tabProps: {
      profile: jobOrder,
      profileModule: EntityType.Joborder,
      onNewItemClick: onSendoutClick,
      onSendoutClick,
      shouldRefresh
    }
  },
  {
    id: TabKeys.SendoutsV2,
    tabProps: {
      profile: jobOrder,
      profileModule: EntityType.Joborder,
      onNewItemClick: onSendoutClick,
      onSendoutClick
    }
  },
  {
    id: TabKeys.Placements,
    tabProps: {
      key: tabKeys.placements,
      onNewPlacementClick,
      onTriggerTabRefresh,
      profileId: jobOrder?.id,
      profileModule: EntityType.Joborder
    }
  },
  {
    id: TabKeys.Candidates,
    tabProps: {
      uiState,
      joborderId: jobOrder?.id,
      endpoint,
      profileName
    }
  },
  {
    id: TabKeys.HiringAuthorities,
    tabProps: {
      profile: jobOrder,
      search: false
    }
  },
  {
    id: TabKeys.ActivityLogs,
    tabProps: {
      itemName: jobOrder?.title,
      onActivityComplete,
      profileId: jobOrder?.id,
      profileModule,
      endpoint
    }
  },
  {
    id: TabKeys.Notes,
    tabProps: {
      itemName: jobOrder?.title,
      onActivityComplete,
      profileId: jobOrder?.id,
      profileModule: EntityType.Joborder,
      endpoint
    }
  },
  {
    id: TabKeys.Tasks,
    tabProps: {
      itemName: jobOrder?.title,
      profileId: joborderId
    }
  },
  {
    id: TabKeys.JobDescription,
    tabProps: {
      jobOrderId: joborderId
    }
  },
  {
    id: TabKeys.MatchMaking,
    tabProps: {
      profileId: joborderId
    }
  }
];

/**
 * @param {Object} props
 * @param {boolean} props.enableUrlNavigation
 * @param {number} props.id
 * @param {Object} props.layoutClassName
 * @param {*} props.match
 * @param {Function} props.onEditionCompleted
 * @param {Function} props.onVerificationCompleted
 */
const FPJobOrderProfile = ({
  enableUrlNavigation,
  id,
  layoutClassName,
  match,
  onEditionCompleted,
  onVerificationCompleted
}) => {
  const location = useLocation();
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const isPresentationMakerEnabled = userHasPermission(Permissions.Inventory.PresentationMaker);
  const [currentUser] = useUserContext();
  const isAIProspectingAssistantEnabled = false; // TODO: Make sure to enable this only for users with the correct permission once the feature is ready, for now we want to hide it from everyone until it's ready.
  const isRoleplayEnabled =
    checkIfFeatureFlagEnabled(FeatureFlags.AIRoleplay) &&
    userHasPermission(Permissions.AIIntegrations.Roleplay);

  const joborderId = id ?? match.params.id;

  const endpoint = `${Endpoints.JobOrders}/${joborderId}`;
  const history = useHistory();
  const dispatch = useDispatch();
  const { referenceRelease } = useSelector(state => state.jobOrder);
  const [isAIDrawerOpen, setIsAIDrawerOpen] = useState(false);
  const [isAIRoleplayDrawerOpen, setIsAIRoleplayDrawerOpen] = useState(false);

  const {
    isOpen: isOpenVerifyUrgencyDialog,
    openDialog: openVerifyUrgencyDialog,
    closeDialog: closeVerifyUrgencyDialog
  } = useVerifyUrgencyDialogManager({
    entity: JobOrderEntity.title
  });
  const { canUseUrgencyVerification, isAddDailyPlanTaskEnabled, shouldShowKeywordTags } =
    useJobOrderProfileFeatureFlags();

  const canUseDailyPlanTask = userHasPermission(Permissions.DailyPlan.CanUseModule);

  /** To keep track of tabs that should refresh after an event.
   * Remember that a component unmount when key changes */
  const [tabKeys, setTabKeys] = useState({
    sendouts: 0,
    placements: 1
  });

  const {
    handleEditClick,
    handleEditClosed,
    handleEditCompleted,
    handleProfileStateUpdate,
    handleReassignClick,
    handleReassignClosed,
    handleReassignCompleted,
    isLoading,
    refreshData: refreshProfile,
    results,
    setUiState,
    state,
    status,
    uiState
  } = useProfile({
    entity: EntityType.Joborder,
    profileId: joborderId,
    includes: `${JobOrderIncludes}${shouldShowKeywordTags ? ',keywordTags' : ''}`,
    shouldLoadTitle: enableUrlNavigation
  });

  const itemName = results?.title;
  const lastActivity = results?.last_activity_date;
  const isAlreadyAccountable = results?.recruiter?.id === currentUser.id;

  const {
    claimAccountabilityMenuItem,
    handleCloseClaimAccountabilityDrawer,
    handleSuccessClaimAccountability,
    openClaimAccountabilityDrawer
  } = useClaimAccountability({
    entity: JobOrderEntity.id,
    isAlreadyAccountable
  });

  const {
    closeDrawer: handleCloseProspectingAssistantDrawer,
    closeModal: handleCloseProspectingAssistantModal,
    handleExecuteProspectingSearch,
    isDrawerOpen: isAIProspectingAssistantDrawerOpen,
    isModalOpen: isProspectingAssistantModalOpen,
    modalProps: prospectingAssistantModalProps,
    openModal: handleOpenProspectingAssistantModal,
    searchText: prospectingSearchText
  } = useProspectingAssistant({
    ...(results ? buildJobOrderProspectingAssistantProps(results) : {}),
    shouldShowProspectingAssistant: isAIProspectingAssistantEnabled
  });

  const handleRefreshActivityStatus = data =>
    handleProfileStateUpdate({ last_activity_date: data?.updated_at });
  const formattedDate = localTimeFormatter(lastActivity, DateFormats.SimpleDateTime);

  const triggerTabRefresh = nameTab =>
    setTabKeys(prevState => ({ ...prevState, [nameTab]: new Date().getTime() }));

  const handleRefreshTable = () => updateUiState({ shouldRefreshTableData: true }, setUiState);
  const { status: dataSheetFormStatus, dataSheet } = useFetchDataSheet(results?.id);
  const useFetchCatalogsInstance = useFetchCatalogs({ companyId: results?.company?.id });

  const isOverviewLoading =
    dataSheetFormStatus === UIStatus.Loading ||
    useFetchCatalogsInstance.uiState === UIStatus.Loading;

  const getInventoryType = () => {
    if (!isOverviewLoading && dataSheet) {
      const jobOrderTypeId = dataSheet?.data.section9.jobOrderType;

      return useFetchCatalogsInstance.catalogs?.jobOrderTypes?.data?.find(
        type => type.id === jobOrderTypeId
      );
    }

    return results?.whiteSheet?.jobOrderType;
  };

  const getJobOrderStatus = () => {
    if (dataSheet && !isOverviewLoading) {
      const jobOrderStatusId = dataSheet?.data.section9.jobOrderStatus;

      return useFetchCatalogsInstance.catalogs?.jobOrderStatuses?.data?.find(
        joStatus => joStatus.id === jobOrderStatusId
      );
    }

    return results?.status;
  };

  const jobOrder = results && {
    ...results,
    status: getJobOrderStatus()
  };

  const handleOpenAIDrawer = () => setIsAIDrawerOpen(true);
  const handleCloseAIDrawer = () => setIsAIDrawerOpen(false);
  const handleOpenAIRoleplayDrawer = () => setIsAIRoleplayDrawerOpen(true);
  const handleCloseAIRoleplayDrawer = () => setIsAIRoleplayDrawerOpen(false);

  /** ::::::::::::::::::::::::::::::::::::::::::::::::::::
   *
   *   P L A C E M E N T   S E T T I N G S
   *
   * :::::::::::::::::::::::::::::::::::::::::::::::::::::
   */

  const referenceReleaseHandler = () => dispatch(hideReferenceReleaseComposerAction());
  const { handleReferenceReleaseComplete } = completeReferenceRelease({
    referenceRelease,
    referenceReleaseHandler
  });

  const handleNewPlacementClick = useCallback(
    ({ sendoutId }) => {
      updateUiState(
        {
          isPlacementOpen: true,
          selectedPlacementId: null,
          selectedSendoutId: sendoutId ? parseInt(sendoutId, 10) : null
        },
        setUiState
      );
    },
    [setUiState]
  );

  const { handlePlacementClosed, handlePlacementCompleted } = getPlacementActions({
    history,
    location,
    onRefreshPlacementTable: handleRefreshTable,
    onTriggerTabRefresh: triggerTabRefresh,
    setUiState
  });

  const handleNewSendoutClick = () => {
    handlePlacementClosed();
    handleSendoutClick();
  };

  /** ::::::::::::::::::::::::::::::::::::::::::::::::::::
   *
   *   D U P L I C A T E   J O B  O R D E R
   *
   * :::::::::::::::::::::::::::::::::::::::::::::::::::::
   */

  const handleDuplicateClick = async () => {
    dispatch(
      confirmAction({
        severity: 'warning',
        ...duplicateDialogCopies,
        cancelButtonText: strings.shared.ui.cancel,
        onConfirm: async ok => {
          try {
            if (!ok) return;

            setUiState(prevState => ({ ...prevState, isLoading: true }));
            const response = await API.post(`${endpoint}/duplicate`);
            if (response.data && response.status === HTTPStatusCodes.Created) {
              dispatch(
                showAlert({
                  severity: 'success',
                  title: strings.inventoryProfiles.sections.jobOrders.successDuplication
                })
              );

              const { id: newJobOrderId } = response.data.data;
              history.push(EntityRoutes.JobOrderProfile.replace(':id', newJobOrderId));
            }
          } catch (error) {
            dispatch(
              showAlert({
                severity: 'error',
                title: 'Job Order',
                autoHideDuration: 5000,
                body: getErrorMessage(error)
              })
            );
          } finally {
            setUiState(prevState => ({ ...prevState, isLoading: false }));
          }
        }
      })
    );
  };
  const userCanEdit = canUserOrStaffEditEntity(currentUser, jobOrder);

  const extraMenuItems = [
    {
      title: 'Duplicate',
      action: handleDuplicateClick,
      visible: userCanEdit
    },
    {
      title: 'Add Action',
      action: () => setUiState(prev => ({ ...prev, isDailyPlanTaskDrawerOpen: true })),
      visible: isAddDailyPlanTaskEnabled && canUseDailyPlanTask
    },
    {
      title: strings.ai.generators.roleplay.actionButton,
      action: handleOpenAIRoleplayDrawer,
      visible: isRoleplayEnabled
    },
    claimAccountabilityMenuItem
  ];

  /** ::::::::::::::::::::::::::::::::::::::::::::::::::::
   *
   *   S E N D O U T S
   *
   * :::::::::::::::::::::::::::::::::::::::::::::::::::::
   */

  const handleSendoutClick = async sendout => {
    if (sendout && sendout.id) {
      const sendoutResponse = await getSendoutById(sendout.id);
      if (!sendoutResponse.data) return;
      const formattedSendout = sendoutDataToForm(sendoutResponse.data, {
        id: sendout?.id,
        title: sendout?.title
      });
      updateUiState(
        {
          isSendoutOpen: true,
          selectedSendout: formattedSendout
        },
        setUiState
      );
      return;
    }
    updateUiState(
      {
        isSendoutOpen: true,
        selectedSendoutId: null,
        selectedSendout: null
      },
      setUiState
    );
  };

  const isSendoutAvailable =
    jobOrder && jobOrder?.company?.type?.id !== CompanyType.NotSigned && uiState.isSendoutOpen;

  const goToCompanyProfile = () =>
    history.push(EntityRoutes.CompanyProfile.replace(':id', jobOrder?.company?.id));

  const handleSendoutCompleted = async () => {
    triggerTabRefresh('sendouts');
    handleRefreshTable();
    handleSendoutClosed();
    await refreshProfile();
  };

  const handleSendoutNavigation = (event, options) => {
    handleSendoutClosed();
    const { sendoutId, placementId } = options || {};
    placementId && handleNewPlacementClick({ id: placementId });
    sendoutId && handleNewPlacementClick({ sendoutId });
  };

  const handleSendoutClosed = () =>
    updateUiState({ isSendoutOpen: false, shouldRefreshTableData: false }, setUiState);

  /** ::::::::::::::::::::::::::::::::::::::::::::::::::::
   *
   *   W H I T E   S H E E T
   *
   * :::::::::::::::::::::::::::::::::::::::::::::::::::::
   */

  const handleDataSheetClick = () => {
    history.push(EntityRoutes.JobOrderEdit.replace(':id', jobOrder?.id));
  };

  /** ::::::::::::::::::::::::::::::::::::::::::::::::::::
   *
   *   T A B S   S E T T I N G S
   *
   * :::::::::::::::::::::::::::::::::::::::::::::::::::::
   */

  const tabsProps = createTabsProps({
    endpoint,
    jobOrder,
    joborderId,
    onActivityComplete: handleRefreshActivityStatus,
    onNewPlacementClick: handleNewPlacementClick,
    onSendoutClick: handleSendoutClick,
    onTriggerTabRefresh: () => triggerTabRefresh(TabKeys.Placements),
    onWriteUpClick: handleDataSheetClick,
    overviewProps: {
      loading: isOverviewLoading || isLoading,
      catalogs: useFetchCatalogsInstance.catalogs,
      dataSheet
    },
    profileModule: EntityType.Joborder,
    profileName: itemName,
    shouldRefresh: uiState.shouldRefreshTableData,
    tabKeys,
    uiState
  });

  const canUserEdit = canUserOrStaffEditEntity(currentUser, jobOrder) || !jobOrder;

  const writeUpActions = (() => {
    return [
      {
        title: editButton,
        action: handleDataSheetClick,
        visible: canUserEdit
      },
      {
        title: 'Verify Urgency',
        action: openVerifyUrgencyDialog,
        visible:
          canUseUrgencyVerification &&
          userHasRoles([Roles.RegionalDirector, Roles.AssistantRegionalDirector, Roles.Leadership])
      }
    ];
  })();

  const headerProps = {
    sectionName: 'Job Order',
    title: itemName
  };

  const handleRefreshProfile = async () => {
    await refreshProfile();
  };

  const finalStatus = isOverviewLoading || status === UIStatus.Loading ? UIStatus.Loading : status;

  const handleTaskDrawerClose = () => {
    setUiState(prev => ({ ...prev, isDailyPlanTaskDrawerOpen: false }));
  };

  const handleOnTaskCreated = () => {
    handleTaskDrawerClose();
    history.push(EntityRoutes.DailyPlan);
  };

  return (
    <>
      <InventoryProfileLayout
        activityLabel={formattedDate}
        addItemLabel="Add a Collaborator"
        additionalHeaderActions={
          <>
            <Box alignItems="center" display="flex" gridGap={16} marginRight={2}>
              {isPresentationMakerEnabled && (
                <FPActionButton
                  icon={SvgMakeAi}
                  onClick={handleOpenAIDrawer}
                  text={strings.ai.generators.presentationsMaker.actionButton}
                  withGradient
                />
              )}
              {isAIProspectingAssistantEnabled && (
                <FPActionButton
                  icon={PublicRoundedIcon}
                  onClick={handleOpenProspectingAssistantModal}
                  text={strings.ai.generators.prospectingAssistant.actionButton}
                  variant="outlined"
                />
              )}
            </Box>
          </>
        }
        contentPageLayoutProps={{ className: layoutClassName }}
        enableUrlNavigation={enableUrlNavigation}
        entityType={JobOrderEntity}
        headerCardProps={headerProps}
        inventoryType={getInventoryType()}
        recruiters={jobOrder?.additionalRecruiters}
        results={jobOrder}
        profileMenuProps={{
          onReassignClick: handleReassignClick,
          onEditClick: handleEditClick,
          customMenuItems: extraMenuItems,
          writeUpActions
        }}
        ProfileTabsProps={{
          endpoint,
          organizationStorageKey: PERSISTENCE_KEYS.PROFILE_TABS.JOB_ORDERS,
          organizationSubheader: `${JobOrderEntity.singular} ${strings.inventoryProfiles.title}`,
          profileTabProps: tabsProps
        }}
        ProfileUiStateManagerProps={{
          status: finalStatus,
          responseStatusCode: state.responseStatusCode,
          entityRoute: EntityRoutes.JobOrders,
          onRefreshProfileClick: handleRefreshProfile,
          entity: JobOrderEntity
        }}
      />
      <When condition={!isLoading && jobOrder?.id}>
        <>
          <When condition={uiState.isEditOpen}>
            <JobOrderEdit
              joborder={jobOrder}
              onEditCompleted={handleEntityEditionCompleted({
                handleEditCompleted,
                enableUrlNavigation,
                onEditionCompleted
              })}
              onEditClosed={handleEditClosed}
            />
          </When>

          <Drawer open={uiState.isSendoutOpen} onClose={handleSendoutClosed}>
            {isSendoutAvailable && uiState.isSendoutOpen && (
              <SendoutsForm
                sendoutData={uiState.selectedSendout}
                profile={jobOrder}
                onCreate={handleSendoutCompleted}
                onEdit={handleSendoutCompleted}
                onNavigate={handleSendoutNavigation}
                onDelete={handleSendoutCompleted}
                onClose={handleSendoutClosed}
                onDuplicateSendoutSelected={handleSendoutClick}
              />
            )}
          </Drawer>

          <When condition={!isSendoutAvailable && uiState.isSendoutOpen}>
            <FPDrawer
              FPDrawerProps={{ open: true, onClose: handleSendoutClosed }}
              CardHeaderProps={{ title: 'New Sendout' }}
              footer={null}
              onClose={handleSendoutClosed}
            >
              <EmptyPlaceholder
                title={strings.inventoryProfiles.sections.jobOrders.notAvailableStrings.title}
                subtitle={
                  strings.inventoryProfiles.sections.jobOrders.notAvailableStrings.description
                }
                customEmptyState={<EmptyFeeAgreement />}
              >
                <FPActionButton
                  text={
                    strings.inventoryProfiles.sections.jobOrders.notAvailableStrings.buttonTitle
                  }
                  type="button"
                  onClick={goToCompanyProfile}
                />
              </EmptyPlaceholder>
            </FPDrawer>
          </When>

          {uiState.isPlacementOpen && !uiState.selectedPlacementId && (
            <Drawer open={uiState.isPlacementOpen} onClose={handlePlacementClosed}>
              <PlacementCreateManager
                sendoutId={uiState.selectedSendoutId}
                companyId={jobOrder?.company?.id}
                jobOrderId={joborderId}
                onClose={handlePlacementClosed}
                onCompleted={handlePlacementCompleted}
                onNewSendoutClick={handleNewSendoutClick}
              />
            </Drawer>
          )}
          {referenceRelease?.candidateId && (
            <ReferenceRelease
              candidateId={referenceRelease.candidateId}
              onClose={referenceReleaseHandler}
              onCompleted={handleReferenceReleaseComplete}
            />
          )}
          <When condition={uiState.isReassignOpen}>
            <ReassignRecruiterForm
              baseEndpoint={Endpoints.JobOrders}
              item={jobOrder}
              entityType={JobOrderEntity}
              onReassignCompleted={handleReassignCompleted}
              onReassignClosed={handleReassignClosed}
            />
          </When>
          {isOpenVerifyUrgencyDialog && (
            <VerifyUrgencyDialog
              entityId={joborderId}
              entityType={EntityType.Joborder}
              open={isOpenVerifyUrgencyDialog}
              onClose={closeVerifyUrgencyDialog}
              onSaveSucceeded={() => {
                closeVerifyUrgencyDialog();
                onVerificationCompleted && onVerificationCompleted();
              }}
            />
          )}
        </>
        {isPresentationMakerEnabled && isAIDrawerOpen && (
          <PresentationMakerTemplate
            buildSourceParamsFn={buildPresentationMakerJobOrderParam}
            generatePresentationFn={generateJobOrderPresentation}
            leftContent={({ onSelectPresentationType, isEnabled }) => (
              <JobOrderPresentationTypeSelector
                isEnabled={isEnabled}
                jobOrder={jobOrder}
                onSelectPresentationType={onSelectPresentationType}
              />
            )}
            onClose={handleCloseAIDrawer}
            sourceData={jobOrder}
          />
        )}

        <When condition={isAIRoleplayDrawerOpen}>
          <PresentationMakerTemplate
            buildSourceParamsFn={buildPresentationMakerJobOrderParam}
            generatePresentationFn={generateJobOrderPresentation}
            leftContent={({ onSelectPresentationType }) => (
              <JobOrderRoleplayScenarioSelector
                jobOrder={jobOrder}
                onSelectPresentationType={onSelectPresentationType}
              />
            )}
            onClose={handleCloseAIRoleplayDrawer}
            rightContentFooter={() => (
              <CallVerbiages callType={CALL_VERBIAGES_TYPES.recruiting.id} />
            )}
            sourceData={jobOrder}
            subtitle={strings.ai.generators.roleplay.subtitle}
            title={strings.ai.generators.roleplay.title}
          />
        </When>

        <When condition={isProspectingAssistantModalOpen && isAIProspectingAssistantEnabled}>
          <AIProspectingAssistantModal
            {...prospectingAssistantModalProps}
            onClose={handleCloseProspectingAssistantModal}
            onExecuteSearch={handleExecuteProspectingSearch}
            open
          />
        </When>

        <When condition={isAIProspectingAssistantDrawerOpen && isAIProspectingAssistantEnabled}>
          <AIProspectingAssistantDrawer
            entityId={joborderId}
            initialSearchText={prospectingSearchText}
            onClose={handleCloseProspectingAssistantDrawer}
            open
          />
        </When>
      </When>
      <When condition={uiState.isDailyPlanTaskDrawerOpen}>
        <CreationTaskDrawer
          allowedTaskTypes={[TASK_TYPES.recruitJobOrder]}
          onClose={handleTaskDrawerClose}
          entityData={{
            id: results.id,
            name: results.title
          }}
          entityType={EntityType.Joborder}
          onTaskCreated={handleOnTaskCreated}
        />
      </When>
      <When condition={openClaimAccountabilityDrawer}>
        <ClaimAccountabilityDrawer
          entityId={joborderId}
          entityType={JobOrderEntity.id}
          onClose={handleCloseClaimAccountabilityDrawer}
          onSuccess={handleSuccessClaimAccountability}
          open={openClaimAccountabilityDrawer}
        />
      </When>
    </>
  );
};

export default FPJobOrderProfile;
